/**
 * Browser client for the Contact Us form (COM-735) — the only file in this
 * module that calls `fetch`.
 *
 * Posts to the Worker's own `/resources/contact-us`, which validates and
 * bridges to SFRA server-side. A plain `fetch`, not a `useFetcher` submit: a
 * successful fetcher action revalidates every loader on the page, and the
 * pages this block lands on are Builder pages whose loaders are the expensive
 * part. Nothing on the page depends on the result.
 *
 * Never throws — every failure is a result the UI can render.
 */
import { upstreamSignal } from "~/utils/upstream-timeouts";

import {
  CONTACT_US_ENDPOINT,
  buildContactUsRequestBody,
  parseContactUsResult,
} from "./contact-us.logic";
import type { ContactUsResult, ContactUsValues } from "./types";

export async function submitContactUs(
  values: ContactUsValues,
): Promise<ContactUsResult> {
  let response: Response;
  try {
    response = await fetch(CONTACT_US_ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: buildContactUsRequestBody(values).toString(),
      signal: upstreamSignal("contact-us"),
    });
  } catch {
    return { ok: false, reason: "unavailable" };
  }
  const json: unknown = await response.json().catch(() => null);
  return parseContactUsResult(json);
}
